/**
 * Sprint 153 — **El total, escrito con letras.**
 *
 * Lo pidió la dueña por WhatsApp, vendiendo:
 *
 * > *"¿Se puede añadir en letras el precio? Me confunde la forma de la letra… me cuesta trabajo
 * > distinguir. Son: 'Ochocientos Cuarenta Pesos 00/100 M.N.', de preferencia. O cambiar el tipo
 * > de letra."*
 *
 * No es una petición de formato: **no puede leer el número**. El total ya sale en negrita y al
 * doble de alto, así que el tamaño no era el problema — lo es la forma de los dígitos en la fuente
 * monoespaciada de una térmica, donde un 3 y un 8 se parecen más de lo que deberían a cierta
 * distancia y con el papel algo gastado.
 *
 * El importe en letras no compite con el número: lo **desambigua**. Es, además, como se ha escrito
 * siempre una nota de venta en México, así que no hay nada que aprender.
 *
 * ## Por qué vive en `shared/`
 *
 * El mismo total se imprime por tres caminos —la térmica del escritorio, el papel del navegador y
 * el PDF— y se enseña en la vista previa. Cuatro sitios escribiendo la misma frase es exactamente
 * lo que el Sprint 141 pagó caro y el 148 volvió a pagar: la vista previa enseñaba una cosa y el
 * papel otra. Una sola función, cuatro consumidores.
 */

const UNIDADES = [
  '',
  'UNO',
  'DOS',
  'TRES',
  'CUATRO',
  'CINCO',
  'SEIS',
  'SIETE',
  'OCHO',
  'NUEVE',
  'DIEZ',
  'ONCE',
  'DOCE',
  'TRECE',
  'CATORCE',
  'QUINCE',
  'DIECISÉIS',
  'DIECISIETE',
  'DIECIOCHO',
  'DIECINUEVE',
  'VEINTE',
];

const DECENAS = [
  '',
  '',
  'VEINTI',
  'TREINTA',
  'CUARENTA',
  'CINCUENTA',
  'SESENTA',
  'SETENTA',
  'OCHENTA',
  'NOVENTA',
];

const CENTENAS = [
  '',
  'CIENTO',
  'DOSCIENTOS',
  'TRESCIENTOS',
  'CUATROCIENTOS',
  'QUINIENTOS',
  'SEISCIENTOS',
  'SETECIENTOS',
  'OCHOCIENTOS',
  'NOVECIENTOS',
];

/** 0–999 en letras. Devuelve cadena vacía para el cero: quien llame decide cómo lo dice. */
function hasta999(n: number): string {
  if (n === 0) return '';
  if (n === 100) return 'CIEN';

  const c = Math.floor(n / 100);
  const resto = n % 100;
  const centena = CENTENAS[c];

  if (resto === 0) return centena;
  if (resto <= 20) return `${centena} ${UNIDADES[resto]}`.trim();

  const d = Math.floor(resto / 10);
  const u = resto % 10;
  // Veintiuno, veintidós… van pegados; de treinta en adelante, con "y".
  const decena = d === 2 ? `VEINTI${UNIDADES[u].toLowerCase().toUpperCase()}` : DECENAS[d];
  if (d === 2) return `${centena} ${u === 0 ? 'VEINTE' : decena}`.trim();
  if (u === 0) return `${centena} ${decena}`.trim();
  return `${centena} ${decena} Y ${UNIDADES[u]}`.trim();
}

/**
 * Un número entero de pesos, en letras.
 *
 * Cubre hasta los millones, que es más de lo que va a ver una nota de restaurante — pero un
 * importe fuera de rango no puede imprimir basura en un papel que se le entrega a un cliente.
 */
function enterosEnLetras(n: number): string {
  if (n === 0) return 'CERO';

  const millones = Math.floor(n / 1_000_000);
  const miles = Math.floor((n % 1_000_000) / 1000);
  const resto = n % 1000;

  const partes: string[] = [];

  if (millones > 0) {
    partes.push(millones === 1 ? 'UN MILLÓN' : `${hasta999(millones)} MILLONES`);
  }
  if (miles > 0) {
    // "MIL", no "UN MIL": nadie dice "un mil pesos".
    partes.push(miles === 1 ? 'MIL' : `${hasta999(miles)} MIL`);
  }
  if (resto > 0) partes.push(hasta999(resto));

  return partes.join(' ').replace(/\s+/g, ' ').trim();
}

/**
 * Centavos → la frase que va en el papel.
 *
 * `84000` → `"OCHOCIENTOS CUARENTA PESOS 00/100 M.N."`
 * `13550` → `"CIENTO TREINTA Y CINCO PESOS 50/100 M.N."`
 * `100`   → `"UN PESO 00/100 M.N."`
 *
 * Los centavos van en cifra sobre 100, como se escriben siempre: deletrearlos alarga la línea sin
 * ayudar a nadie, y es la forma que la dueña escribió en su mensaje.
 *
 * Un importe negativo —una devolución— se dice con todas sus letras en vez de esconder el signo
 * menos delante de un número: en un papel, un "-" se confunde con una raya de la impresora.
 */
export function totalEnLetras(centavos: number): string {
  if (!Number.isFinite(centavos)) return '';

  const negativo = centavos < 0;
  const abs = Math.round(Math.abs(centavos));
  const pesos = Math.floor(abs / 100);
  const cent = abs % 100;

  /**
   * El apócope: delante de "peso(s)" no se dice "uno", se dice **un**.
   *
   * *Un peso*, *veintiún pesos*, *treinta y un pesos*, *ciento un pesos*, *mil un pesos*. Sólo
   * aplica al "UNO" que queda **pegado al sustantivo**: en "VEINTIUNO MIL" no habría apócope, pero
   * ese caso no existe porque los miles ya se arman aparte.
   */
  const letras = enterosEnLetras(pesos)
    .replace(/VEINTIUNO$/, 'VEINTIÚN')
    .replace(/(^|\s)UNO$/, '$1UN');
  const moneda = pesos === 1 ? 'PESO' : 'PESOS';
  const frase = `${letras} ${moneda} ${String(cent).padStart(2, '0')}/100 M.N.`;

  return negativo ? `MENOS ${frase}` : frase;
}
